import { Config, ICheckableItem, ICheckboxGroup, isCheckboxGroup } from '../domain/entities/Config';
import { IAppConfig } from '@estee/elc-universal-utils';
import { query, serviceQueries } from '@estee/elc-service-bus';
import {
    brandIdValPath,
    languageIdValPath,
    rampFormConfigName,
    regionIdValPath
} from '../../constants/Constants';
import { computed, observable } from 'mobx';
import { IUserFormValues } from '../domain/entities/UserFormStore';

export class ConfigRepository {
    @observable public config: Config;

    constructor(config: IAppConfig) {
        this.config = new Config(config);
    }

    public getDefaultConfigs = async () => {
        const config = await query(serviceQueries.GET_CONFIG, [
            rampFormConfigName,
            languageIdValPath,
            brandIdValPath,
            regionIdValPath
        ]);

        // HACK: due to a known issue of 'DeepSearchResolver`, query may return unexpected nested config value,
        // and adding custom mapping is not recommended,
        // see: https://bitbucket.esteeonline.com/projects/STARDUST/repos/fe-elc-service-config-manager/pull-requests/55/overview
        if (config[rampFormConfigName] === null || typeof config[rampFormConfigName] !== 'object') {
            // abandon 'rampForm' from 'pgApiTokens'
            delete config[rampFormConfigName];
        }

        this.config.updateConfig(config);
    };

    // TODO: refactor to avoid repeated sections reduce processing (APACFTRS1-770)
    @computed
    public get signUpFormInitialValues(): IUserFormValues {
        if (!this.rampConfig?.signUpFormInputSchema) {
            return {};
        } else {
            const {
                signUpFormInputSchema: { sections }
            } = this.rampConfig;

            return sections.reduce((values, section) => {
                return section.items.reduce((sectionValues, item) => {
                    if (isCheckboxGroup(item)) {
                        let initialValues = sectionValues;
                        const { items, selectAll } = item;
                        if (selectAll) {
                            const itemValues = new Set(items.map((n) => n.initialValue));
                            // Make sure select all checked status consist with sub-items checked status
                            const selectAllValue =
                                itemValues.size === 1 ? !!Array.from(itemValues)[0] : false;
                            initialValues = {
                                ...initialValues,
                                [selectAll.name]: selectAllValue
                            };
                        }

                        return item.items.reduce(
                            (checkboxGroupValues, checkbox) => ({
                                ...checkboxGroupValues,
                                [checkbox.name]: checkbox.initialValue || false
                            }),
                            { ...initialValues }
                        );
                    } else {
                        return item.initialValue === undefined
                            ? sectionValues
                            : { ...sectionValues, [item.name]: item.initialValue };
                    }
                }, values);
            }, {});
        }
    }

    // This data is determined when RAMP configurations are set, and will not be updated. Returns a collection of checkbox field names whose checked value should be inverse.
    // Note: Not used in Reactive Context so setting this as computed does not take advantage of caching.
    private _inverseFields: string[];
    public get inverseFields() {
        if (this._inverseFields) {
            return this._inverseFields;
        }
        if (!this.rampConfig?.signUpFormInputSchema) {
            return [];
        }
        const {
            signUpFormInputSchema: { sections }
        } = this.rampConfig;
        this._inverseFields = [];
        sections.forEach((section) => {
            section.items.forEach((item) => {
                const { name, inputType } = item;
                if (inputType === 'checkbox') {
                    const { inverseCheckedValue } = item as ICheckableItem;
                    inverseCheckedValue && this._inverseFields.push(name);
                }
                if (inputType === 'checkboxGroup') {
                    const { items, inverseCheckedValue } = item as ICheckboxGroup;
                    inverseCheckedValue &&
                        items.forEach((groupItem) => {
                            this._inverseFields.push(groupItem.name);
                        });
                }
            });
        });

        return this._inverseFields;
    }

    @computed public get rampConfig() {
        return this.config.config.rampForm;
    }

    @computed public get userInfoDisplay() {
        return this.rampConfig?.userInfoDisplay || { mobile: '', name: '', email: '' };
    }
}
